import { useState } from 'react'
import { ErrorState, LoadingState } from '../components/StateViews'
import { useApi } from '../lib/useApi'

type Decision = {
  variantId: string
  detectedAt: number
  availableQty: number
  subscriberCount: number
  alertsSent: boolean
  reason: string | null
  explanation: string | null
}

type Payload = {
  shopDomain: string
  shopName: string | null
  plan: string
  planName: string
  installedAt: number
  installedDaysAgo: number
  widgetConfigPublished: boolean
  alertsUsed: number
  alertQuota: number | null
  quotaExceeded: boolean
  environment: string
  recentDecisions: Decision[]
  generatedAt: number
}

const SUPPORT_EMAIL = 'contact@stockwait.com'

/**
 * Contact support, with the diagnostic bundle already attached (SW-1004).
 *
 * The merchant never has to answer "what's your store URL?", "what plan are you on?" or "is the
 * app embed enabled?"; those cost a round trip each, on every ticket, and the merchant is the
 * person least able to answer the last one.
 *
 * The bundle is shown in full rather than attached invisibly. A merchant about to send us their
 * data is entitled to see exactly what they are sending, and it contains no shopper data, only
 * their own shop's configuration, so there is nothing to hide and showing it builds the trust
 * the privacy policy claims.
 */
export function Support() {
  const { data, error, loading, reload } = useApi<Payload>('/support/context')
  const [copied, setCopied] = useState(false)

  if (loading && !data) return <LoadingState />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data) return null

  const bundle = JSON.stringify(data, null, 2)

  const summary = [
    `Shop: ${data.shopDomain}`,
    `Plan: ${data.planName}`,
    `Installed: ${data.installedDaysAgo} day(s) ago`,
    `Storefront config published: ${data.widgetConfigPublished ? 'yes' : 'no'}`,
    `Alerts used: ${data.alertsUsed}${data.alertQuota === null ? ' (unlimited)' : ` of ${data.alertQuota}`}`,
  ].join('\n')

  const mailto =
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent(`StockWait support: ${data.shopDomain}`)}` +
    `&body=${encodeURIComponent(`Describe the problem here.\n\n---\nDiagnostics (please keep)\n${summary}\n`)}`

  async function copy() {
    try {
      await navigator.clipboard.writeText(bundle)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be blocked inside the admin iframe. The textarea below is always
      // selectable, so a failure here costs the merchant a manual select rather than the ticket.
      setCopied(false)
    }
  }

  return (
    <s-stack direction="block" gap="large">
      <s-section heading="Contact support">
        <s-stack direction="block" gap="base">
          <s-paragraph>
            We reply within one business day. If your alerts are not sending, say so in the subject
            Those are answered within four business hours, because you are losing sales while you
            wait.
          </s-paragraph>
          <s-stack direction="inline" gap="base">
            <s-button variant="primary" href={mailto}>
              Email support
            </s-button>
            <s-button variant="secondary" onClick={copy}>
              {copied ? 'Copied' : 'Copy diagnostics'}
            </s-button>
          </s-stack>
        </s-stack>
      </s-section>

      <s-section heading="Recent restock decisions">
        <s-stack direction="block" gap="base">
          <s-paragraph>
            The most common question is "why didn't my customers get an email?". This is the answer
            for the last five restocks we saw.
          </s-paragraph>
          {data.recentDecisions.length === 0 ? (
            <s-paragraph tone="neutral">
              No restocks recorded yet. If you expected one, the usual cause is the app embed being
              switched off in your theme.
            </s-paragraph>
          ) : (
            <s-table variant="auto">
              <s-table-header-row>
                <s-table-header listSlot="primary">Variant</s-table-header>
                <s-table-header listSlot="labeled">Units</s-table-header>
                <s-table-header listSlot="labeled">Waiting</s-table-header>
                <s-table-header listSlot="labeled">Outcome</s-table-header>
              </s-table-header-row>
              <s-table-body>
                {data.recentDecisions.map((d) => (
                  <s-table-row key={`${d.variantId}-${d.detectedAt}`}>
                    <s-table-cell>{d.variantId}</s-table-cell>
                    <s-table-cell>{d.availableQty}</s-table-cell>
                    <s-table-cell>{d.subscriberCount}</s-table-cell>
                    <s-table-cell>{d.explanation ?? 'Unknown'}</s-table-cell>
                  </s-table-row>
                ))}
              </s-table-body>
            </s-table>
          )}
        </s-stack>
      </s-section>

      <s-section heading="What we send with your request">
        <s-stack direction="block" gap="base">
          <s-paragraph>
            Your shop's configuration only, with no customer email addresses and no subscriber data.
          </s-paragraph>
          <s-text-area label="Diagnostics" name="diagnostics" rows={10} value={bundle} readOnly />
        </s-stack>
      </s-section>
    </s-stack>
  )
}
